import { createPortal } from 'react-dom'
import {
  Archive,
  Check,
  CheckCircle2,
  ClipboardCheck,
  Copy,
  Diamond,
  Flame,
  Gem,
  Globe2,
  Mail,
  Phone,
  Search,
  UsersRound,
  X,
  type LucideIcon,
} from 'lucide-react'
import { useEffect, useMemo, useRef, useState, type CSSProperties, type MouseEvent, type PointerEvent as ReactPointerEvent, type WheelEvent as ReactWheelEvent } from 'react'
import { useSearchParams } from 'react-router-dom'
import type { ProspectingDataState } from '../hooks/useProspectingData'
import { ProspectingFilter } from '../features/prospecting/ProspectingFilter'
import { WolfMark } from '../components/brand/WolfMark'
import type {
  PlatinumBillingKey,
  ProspectingListId,
  ProspectingRecord,
  ProspectingViewId,
} from '../types/prospecting'
import '../features/prospecting/prospecting.css'
type ViewMeta = {
  label: string
  shortLabel: string
  description: string
  icon: LucideIcon
}

const VIEW_META: Record<ProspectingViewId, ViewMeta> = {
  hot: {
    label: 'Hot',
    shortLabel: 'Hot',
    description: 'The opportunities that deserve attention first.',
    icon: Flame,
  },
  audit: {
    label: 'Audit',
    shortLabel: 'Audit',
    description: 'Prospects being prepared for or sent an audit.',
    icon: ClipboardCheck,
  },
  leads: {
    label: 'Leads',
    shortLabel: 'Leads',
    description: 'The current working prospect list.',
    icon: UsersRound,
  },
  old: {
    label: 'Old Leads',
    shortLabel: 'Old',
    description: 'Past opportunities worth another conversation.',
    icon: Archive,
  },
  platinum: {
    label: 'Platinum',
    shortLabel: 'Platinum',
    description: "William's premium offer and pricing reference.",
    icon: Gem,
  },
}

const VIEW_ORDER: ProspectingViewId[] = ['hot', 'audit', 'leads', 'old', 'platinum']
const BILLING_LABELS: Record<PlatinumBillingKey, string> = {
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  sixMonths: '6 Months',
  annual: 'Annual',
}

const PLATINUM_BILLING_ORDER: PlatinumBillingKey[] = [
  'monthly',
  'quarterly',
  'sixMonths',
  'annual',
]
function isView(value: string | null): value is ProspectingViewId {
  return VIEW_ORDER.includes(value as ProspectingViewId)
}

function firstEmail(value: string): string | null {
  return value.match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i)?.[0] ?? null
}

function externalUrl(value: string): string | null {
  const raw = value.trim()
  if (!raw || /^(na|old calls)$/i.test(raw)) return null

  const first = raw.match(/https?:\/\/[^\s]+/i)?.[0] ?? raw.split(/\s+or\s+|\s+and\s+/i)[0]
  if (!first) return null

  const withProtocol = /^https?:\/\//i.test(first) ? first : `https://${first}`
  try {
    return new URL(withProtocol).toString()
  } catch {
    return null
  }
}

function phoneUrl(value: string): string | null {
  const cleaned = value.replace(/[^\d+]/g, '')
  return cleaned.replace(/\D/g, '').length >= 7 ? `tel:${cleaned}` : null
}


function money(value?: number): string {
  if (!value) return 'â€”'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value)
}

function FacebookIcon({ size = 18 }: { size?: number }) {
  return (
    <svg
      aria-hidden="true"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      focusable="false"
    >
      <circle
        cx="12"
        cy="12"
        r="10"
        fill="#FFFFFF"
        stroke="none"
        strokeWidth="0"
      />
      <path
        d="M13.35 20V12.7H15.8L16.15 9.85H13.35V8.05C13.35 7.23 13.58 6.66 14.76 6.66H16.25V4.11C15.99 4.07 15.09 4 14.04 4C11.85 4 10.35 5.34 10.35 7.79V9.85H8V12.7H10.35V20H13.35Z"
        fill="#111111"
      />
    </svg>
  )
}
function ContactActions({ record }: { record: ProspectingRecord }) {
  const profile = externalUrl(record.profile)
  const website = externalUrl(record.website)
  const email = firstEmail(record.email)
  const phone = phoneUrl(record.phone)
  const gmail = email
    ? `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(email)}`
    : null

  const stop = (event: MouseEvent<HTMLAnchorElement>) => event.stopPropagation()

  return (
    <div className="prospecting-actions">
      {profile ? (
        <a href={profile} target="_blank" rel="noreferrer" onClick={stop} title="Open Facebook profile" aria-label={`Open ${record.person} Facebook profile`}>
          <FacebookIcon size={18} />
        </a>
      ) : null}
      {gmail ? (
        <a href={gmail} target="_blank" rel="noreferrer" onClick={stop} title="Compose email" aria-label={`Email ${record.person}`}>
          <Mail aria-hidden="true" size={17} />
        </a>
      ) : null}
      {phone ? (
        <a href={phone} onClick={stop} title="Call" aria-label={`Call ${record.person}`}>
          <Phone aria-hidden="true" size={17} />
        </a>
      ) : null}
      {website ? (
        <a href={website} target="_blank" rel="noreferrer" onClick={stop} title="Open website" aria-label={`Open ${record.person} website`}>
          <Globe2 aria-hidden="true" size={17} />
        </a>
      ) : null}
      {!profile && !gmail && !phone && !website ? <span>NA</span> : null}
    </div>
  )
}

function DetailModal({
  record,
  memberships,
  inCrm,
  onClose,
}: {
  record: ProspectingRecord
  memberships: ProspectingListId[]
  inCrm: boolean
  onClose: () => void
}) {
  useEffect(() => {
    const html = document.documentElement
    const body = document.body

    const previousHtmlOverflow = html.style.overflow
    const previousBodyOverflow = body.style.overflow
    const previousHtmlOverscroll = html.style.overscrollBehavior
    const previousBodyOverscroll = body.style.overscrollBehavior

    html.style.overflow = 'hidden'
    body.style.overflow = 'hidden'
    html.style.overscrollBehavior = 'none'
    body.style.overscrollBehavior = 'none'

    const handleEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        onClose()
      }
    }

    document.addEventListener('keydown', handleEscape)

    return () => {
      document.removeEventListener('keydown', handleEscape)

      html.style.overflow = previousHtmlOverflow
      body.style.overflow = previousBodyOverflow
      html.style.overscrollBehavior = previousHtmlOverscroll
      body.style.overscrollBehavior = previousBodyOverscroll
    }
  }, [onClose])

  const profile = externalUrl(record.profile)
  const website = externalUrl(record.website)
  const email = firstEmail(record.email)
  const phone = phoneUrl(record.phone)

  // "Leads" on its own is reserved for the Main CRM, so the prospecting Leads
  // tab is named in full here.
  const sourceLists = (memberships.length ? memberships : [record.list])
    .map((list) => (list === 'leads' ? 'Prospecting Leads' : VIEW_META[list].label))
    .join(' · ')

  return createPortal(
    <div
      className="prospecting-modal-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
      role="presentation"
    >
      <section
        className="lead-modal prospecting-lead-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="prospecting-detail-title"
      >
        <header className="lead-modal-header">
          <div className="prospecting-lead-avatar" aria-hidden="true">
            {website ? <Globe2 size={27} /> : <UsersRound size={27} />}
          </div>

          <div className="lead-modal-heading">
            <h2 id="prospecting-detail-title">{record.person}</h2>
            <p>Available information about this prospect</p>
          </div>

          <button
            className="icon-button lead-modal-close"
            onClick={onClose}
            title="Close"
            type="button"
            aria-label="Close prospect details"
          >
            <X size={21} />
          </button>
        </header>

        <div className="lead-modal-content">
          <section
            className="lead-modal-section"
            aria-labelledby="prospecting-organization-title"
          >
            <h3 id="prospecting-organization-title">Organization</h3>

            <div className="prospecting-lead-info-grid">
              <div className="prospecting-lead-info-item">
                <UsersRound aria-hidden="true" size={19} />
                <div>
                  <small>Prospect</small>
                  <strong>{record.person}</strong>
                </div>
              </div>

              {profile ? (
                <div className="prospecting-lead-info-item">
                  <FacebookIcon size={19} />
                  <div>
                    <small>Facebook</small>
                    <a
                      href={profile}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Open Facebook profile
                    </a>
                  </div>
                </div>
              ) : null}

              {website ? (
                <div className="prospecting-lead-info-item">
                  <Globe2 aria-hidden="true" size={19} />
                  <div>
                    <small>Website</small>
                    <a
                      href={website}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {record.website}
                    </a>
                  </div>
                </div>
              ) : null}

              {email ? (
                <div className="prospecting-lead-info-item">
                  <Mail aria-hidden="true" size={19} />
                  <div>
                    <small>Email</small>
                    <a href={`mailto:${email}`}>
                      {email}
                    </a>
                  </div>
                </div>
              ) : null}

              {phone ? (
                <div className="prospecting-lead-info-item">
                  <Phone aria-hidden="true" size={19} />
                  <div>
                    <small>Phone</small>
                    <a href={phone}>
                      {record.phone}
                    </a>
                  </div>
                </div>
              ) : null}
            </div>
          </section>

          <section className="lead-modal-section prospecting-lead-facts-section">
            <dl className="lead-modal-facts prospecting-lead-facts">
              <div>
                <dt>Team</dt>
                <dd>{record.team || 'NA'}</dd>
              </div>

              <div>
                <dt>Niche</dt>
                <dd>{record.niche || 'NA'}</dd>
              </div>

              <div>
                <dt>Date</dt>
                <dd>{record.date || 'NA'}</dd>
              </div>

              <div>
                <dt>Audit</dt>
                <dd>{record.auditSent ? 'Audit Sent' : 'Audit Pending'}</dd>
              </div>

              <div>
                <dt>Booked Call</dt>
                <dd>{record.bookedCall ? 'Booked' : 'Not Booked'}</dd>
              </div>

              <div>
                <dt>Source</dt>
                <dd>{sourceLists}</dd>
              </div>

              <div>
                <dt>Main CRM</dt>
                <dd>{inCrm ? 'Also in Leads' : 'Not in CRM'}</dd>
              </div>
            </dl>
          </section>

          {record.needs ? (
            <section
              className="lead-modal-section lead-modal-notes"
              aria-labelledby="prospecting-notes-title"
            >
              <h3 id="prospecting-notes-title">Notes</h3>
              <p>{record.needs}</p>
            </section>
          ) : null}
        </div>
      </section>
    </div>,
    document.body,
  )
}
function Skeleton() {
  return (
    <div
      className="prospecting-dots-loader"
      role="status"
      aria-label="Loading prospecting data"
    >
      <span className="prospecting-dots-loader-inner" aria-hidden="true">
        <i />
        <i />
        <i />
      </span>
    </div>
  )
}
export function ProspectingPage({
  crmMembership,
  dataState,
}: {
  /** Prospecting identity keys that already exist in the Main CRM dataset. */
  crmMembership: ReadonlySet<string>
  dataState: ProspectingDataState
}) {
  const [searchParams, setSearchParams] = useSearchParams()
  const requestedView = searchParams.get('list')
  const activeView: ProspectingViewId = isView(requestedView) ? requestedView : 'hot'

  const { enableReconciliation } = dataState
  useEffect(() => {
    enableReconciliation()
  }, [enableReconciliation])

  const [query, setQuery] = useState('')
  const [niche, setNiche] = useState('all')
  const [team, setTeam] = useState('all')
  const [status, setStatus] = useState('all')
  const [selected, setSelected] = useState<ProspectingRecord | null>(null)
  const [copiedPlan, setCopiedPlan] = useState<string | null>(null)
  const [expandedPlanId, setExpandedPlanId] = useState<string | null>(null)

  const [platinumWheelStep, setPlatinumWheelStep] = useState(0)
  const [platinumDragging, setPlatinumDragging] = useState(false)

  const platinumPointerStartXRef = useRef<number | null>(null)
  const platinumPointerStartYRef = useRef<number | null>(null)

  const platinumWheelLockRef = useRef(false)
  const platinumSuppressClickRef = useRef(false)
  const activeList = activeView === 'platinum' ? null : activeView
  const currentRows = useMemo(
    () => (dataState.data && activeList ? dataState.data.lists[activeList] : []),
    [activeList, dataState.data],
  )

  const niches = useMemo(
    () => Array.from(new Set(currentRows.map((row) => row.niche).filter(Boolean))).sort(),
    [currentRows],
  )
  const teams = useMemo(
    () => Array.from(new Set(currentRows.map((row) => row.team).filter(Boolean))).sort(),
    [currentRows],
  )

  const filteredRows = useMemo(() => {
    const q = query.trim().toLowerCase()

    return currentRows
      .filter((row) => {
        if (niche !== 'all' && row.niche !== niche) return false
        if (team !== 'all' && row.team !== team) return false
        if (status === 'needs-audit' && row.auditSent) return false
        if (status === 'audit-sent' && !row.auditSent) return false
        if (status === 'booked' && !row.bookedCall) return false
        if (status === 'missing-contact' && (firstEmail(row.email) || phoneUrl(row.phone))) return false
        if (status === 'missing-website' && externalUrl(row.website)) return false

        if (!q) return true
        return [
          row.person,
          row.team,
          row.email,
          row.niche,
          row.phone,
          row.needs,
          row.website,
        ]
          .join(' ')
          .toLowerCase()
          .includes(q)
      })
      .sort((a, b) => a.person.localeCompare(b.person))
  }, [currentRows, niche, query, status, team])

  useEffect(() => {
    setNiche('all')
    setTeam('all')
    setStatus('all')

    setExpandedPlanId(null)
    setPlatinumWheelStep(0)
    setPlatinumDragging(false)
  }, [activeView])
  const selectView = (view: ProspectingViewId) => {
    const next = new URLSearchParams(searchParams)
    next.set('list', view)
    setSearchParams(next, { replace: true })
  }

  const copyPlan = async (planId: string, billingKey: PlatinumBillingKey) => {
    const plan = dataState.data?.platinum.find((item) => item.id === planId)
    if (!plan) return

    const text = [
      plan.title,
      ...plan.features.map((feature) => `\u2022 ${feature}`),
      '',
      `${BILLING_LABELS[billingKey]}: ${money(plan.prices[billingKey])}`,
    ].join('\n')

    try {
      await navigator.clipboard.writeText(text)
      setCopiedPlan(planId)
      window.setTimeout(() => setCopiedPlan(null), 1600)
    } catch {
      setCopiedPlan(null)
    }
  }

  const platinumPlans = useMemo(
    () => dataState.data?.platinum ?? [],
    [dataState.data],
  )

  const platinumOffers = useMemo(
    () =>
      platinumPlans.flatMap((plan, planIndex) =>
        PLATINUM_BILLING_ORDER.map((billingKey) => ({
          id: `${plan.id}-${billingKey}`,
          plan,
          planIndex,
          billingKey,
        })),
      ),
    [platinumPlans],
  )

  const platinumOfferCount =
    platinumOffers.length

  const activeOfferIndex =
    platinumOfferCount
      ? (
          (
            platinumWheelStep %
            platinumOfferCount
          ) +
          platinumOfferCount
        ) %
        platinumOfferCount
      : 0

  const activeOffer =
    platinumOffers[activeOfferIndex] ??
    null

  const normalizePlatinumDelta = (
    index: number,
  ) => {
    if (!platinumOfferCount) return 0

    let delta =
      index - activeOfferIndex

    const half =
      platinumOfferCount / 2

    if (delta > half) {
      delta -= platinumOfferCount
    }

    if (delta < -half) {
      delta += platinumOfferCount
    }

    return delta
  }

  const movePlatinum = (
    direction: number,
  ) => {
    if (!platinumOfferCount) return

    setExpandedPlanId(null)
    setPlatinumDragging(false)

    setPlatinumWheelStep(
      (current) =>
        current + direction,
    )
  }

  const selectPlatinumOffer = (
    index: number,
  ) => {
    const delta =
      normalizePlatinumDelta(index)

    if (delta === 0) {
      const offer =
        platinumOffers[index]

      if (!offer) return

      setExpandedPlanId((current) =>
        current === offer.id
          ? null
          : offer.id,
      )

      return
    }

    setExpandedPlanId(null)

    setPlatinumWheelStep(
      (current) =>
        current + delta,
    )
  }

  const handlePlatinumWheel = (
    event: ReactWheelEvent<HTMLDivElement>,
  ) => {
    const delta =
      Math.abs(event.deltaY) >=
      Math.abs(event.deltaX)
        ? event.deltaY
        : event.deltaX

    if (Math.abs(delta) < 4) {
      return
    }

    event.preventDefault()

    if (
      platinumWheelLockRef.current
    ) {
      return
    }

    platinumWheelLockRef.current =
      true

    movePlatinum(
      delta > 0 ? 1 : -1,
    )

    window.setTimeout(() => {
      platinumWheelLockRef.current =
        false
    }, 260)
  }

  const handlePlatinumPointerDown = (
    event: ReactPointerEvent<HTMLDivElement>,
  ) => {
    if (
      event.pointerType === 'mouse' &&
      event.button !== 0
    ) {
      return
    }

    platinumPointerStartXRef.current =
      event.clientX

    platinumPointerStartYRef.current =
      event.clientY

    setPlatinumDragging(false)
  }

  const handlePlatinumPointerMove = (
    event: ReactPointerEvent<HTMLDivElement>,
  ) => {
    const startX =
      platinumPointerStartXRef.current

    const startY =
      platinumPointerStartYRef.current

    if (
      startX === null ||
      startY === null
    ) {
      return
    }

    const dx =
      event.clientX - startX

    const dy =
      event.clientY - startY

    if (
      Math.abs(dx) > 8 &&
      Math.abs(dx) >
        Math.abs(dy)
    ) {
      if (
        !event.currentTarget.hasPointerCapture(
          event.pointerId,
        )
      ) {
        event.currentTarget.setPointerCapture(
          event.pointerId,
        )
      }

      setPlatinumDragging(true)
    }
  }

  const handlePlatinumPointerUp = (
    event: ReactPointerEvent<HTMLDivElement>,
  ) => {
    const startX =
      platinumPointerStartXRef.current

    const startY =
      platinumPointerStartYRef.current

    if (
      startX === null ||
      startY === null
    ) {
      return
    }

    const dx =
      event.clientX - startX

    const dy =
      event.clientY - startY

    if (
      event.currentTarget.hasPointerCapture(
        event.pointerId,
      )
    ) {
      event.currentTarget.releasePointerCapture(
        event.pointerId,
      )
    }

    platinumPointerStartXRef.current =
      null

    platinumPointerStartYRef.current =
      null

    const isSwipe =
      Math.abs(dx) >= 42 &&
      Math.abs(dx) >
        Math.abs(dy)

    if (isSwipe) {
      platinumSuppressClickRef.current =
        true

      movePlatinum(
        dx < 0 ? 1 : -1,
      )

      window.setTimeout(() => {
        platinumSuppressClickRef.current =
          false
      }, 80)

      return
    }

    setPlatinumDragging(false)
  }

  const handlePlatinumPointerCancel = (
    event: ReactPointerEvent<HTMLDivElement>,
  ) => {
    if (
      event.currentTarget.hasPointerCapture(
        event.pointerId,
      )
    ) {
      event.currentTarget.releasePointerCapture(
        event.pointerId,
      )
    }

    platinumPointerStartXRef.current =
      null

    platinumPointerStartYRef.current =
      null

    setPlatinumDragging(false)
  }
  useEffect(() => {
    if (activeView !== 'platinum') {
      return
    }

    const wfPlatinumPreviousBodyOverflow =
      document.body.style.overflow

    const wfPlatinumPreviousHtmlOverflow =
      document.documentElement.style.overflow

    document.body.style.overflow = 'hidden'
    document.documentElement.style.overflow = 'hidden'

    return () => {
      document.body.style.overflow =
        wfPlatinumPreviousBodyOverflow

      document.documentElement.style.overflow =
        wfPlatinumPreviousHtmlOverflow
    }
  }, [activeView])
  const meta = VIEW_META[activeView]
  const ActiveIcon = meta.icon

  return (
    <section className="prospecting-page">
      <header className="prospecting-hero">
        <div>

          <h1>
            Work the next opportunity, <span className="prospecting-william">William.</span>
          </h1>
          <p>Hot prospects, audits, working leads, re-engagement, and Platinum pricing in one workspace.</p>
        </div>

        
      </header>

      <nav className="prospecting-tabs" aria-label="Prospecting lists">
        {VIEW_ORDER.map((view) => {
          const item = VIEW_META[view]
          const Icon = item.icon
          const count = view === 'platinum' ? undefined : dataState.data?.lists[view].length

          return (
            <button
              className={`prospecting-tab${activeView === view ? ' is-active' : ''}`}
              aria-current={activeView === view ? 'page' : undefined}
              key={view}
              onClick={() => selectView(view)}
              type="button"
            >
              <Icon aria-hidden="true" size={17} />
              <span className="prospecting-full-label">{item.label}</span>
              <span className="prospecting-short-label">{item.shortLabel}</span>
              {count !== undefined ? <small>{count}</small> : null}
            </button>
          )
        })}
      </nav>

      <div className={`prospecting-view${activeView === 'hot' ? ' is-hot-view' : ''}`} key={activeView}>
        <div className="prospecting-section-heading">
          <div>
            <span className={`prospecting-section-icon${activeView === 'hot' ? ' is-hot' : ''}`}><ActiveIcon aria-hidden="true" size={18} /></span>
            <div>
              <h2>{meta.label}</h2>
              <p>{meta.description}</p>
            </div>
          </div>
          {activeList && dataState.data ? <strong>{filteredRows.length} of {currentRows.length}</strong> : null}
        </div>

        {dataState.status === 'loading' && !dataState.data ? <Skeleton /> : null}

        {dataState.status === 'error' && !dataState.data ? (
          <div className="prospecting-error">
            <strong>Prospecting could not load.</strong>
            <p>{dataState.error}</p>
            <button onClick={dataState.refresh} type="button">Try again</button>
          </div>
        ) : null}

        {dataState.data && activeList ? (
          <>
            <div className="prospecting-toolbar">
              <label className="prospecting-search">
                <Search aria-hidden="true" size={18} />
                <input
                  type="search"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Search person, company, email..."
                />
              </label>

              <ProspectingFilter
                ariaLabel="Filter by niche"
                value={niche}
                onChange={setNiche}
                options={[
                  { value: 'all', label: 'All niches' },
                  ...niches.map((value) => ({
                    value,
                    label: value,
                  })),
                ]}
              />

              <ProspectingFilter
                ariaLabel="Filter by team"
                value={team}
                onChange={setTeam}
                options={[
                  { value: 'all', label: 'All team' },
                  ...teams.map((value) => ({
                    value,
                    label: value,
                  })),
                ]}
              />

              <ProspectingFilter
                ariaLabel="Filter by status"
                value={status}
                onChange={setStatus}
                options={[
                  { value: 'all', label: 'All status' },
                  { value: 'needs-audit', label: 'Needs audit' },
                  { value: 'audit-sent', label: 'Audit sent' },
                  { value: 'booked', label: 'Booked call' },
                  { value: 'missing-contact', label: 'Missing contact' },
                  { value: 'missing-website', label: 'Missing website' },
                ]}
              />
            </div>

            <div className="prospecting-table">
              <div className="prospecting-table-head" aria-hidden="true">
                <span>Prospect</span><span>Niche</span><span>Team</span><span>Status</span><span>Actions</span>
              </div>

              {filteredRows.length ? filteredRows.map((record) => {
                const memberships = dataState.data?.memberships[record.identityKey] ?? [record.list]
                // "Also in Leads" means the Main CRM. Membership of the
                // prospecting Leads tab is internal and never shown as a badge.
                const alsoIn = memberships
                  .filter((list) => list !== record.list && list !== 'leads')
                  .map((list) => VIEW_META[list].label)
                if (crmMembership.has(record.identityKey)) alsoIn.push(VIEW_META.leads.label)

                return (
                  <article className="prospecting-row" key={record.id}>
                    <button className="prospecting-row-main" onClick={() => setSelected(record)} type="button">
                      <span className="prospecting-person">
                        <strong>{record.person}</strong>
                        <small>{record.date || 'No date'}{record.needs ? ` · ${record.needs}` : ''}</small>
                        {alsoIn.length ? <em>Also in {alsoIn.join(' · ')}</em> : null}
                      </span>
                      <span className="prospecting-niche">{record.niche || 'NA'}</span>
                      <span className="prospecting-team">{record.team || 'NA'}</span>
                      <span className="prospecting-statuses">
                        {record.bookedCall ? <b className="is-booked"><CheckCircle2 size={14} />Booked</b> : null}
                        {record.auditSent ? <b><Check size={14} />Audit Sent</b> : <b className="is-muted">Audit Pending</b>}
                      </span>
                    </button>
                    <ContactActions record={record} />
                  </article>
                )
              }) : (
                <div className="prospecting-empty">
                  <Search aria-hidden="true" size={24} />
                  <strong>No matching prospects.</strong>
                  <p>Clear a filter or search for another prospect.</p>
                </div>
              )}
            </div>
          </>
        ) : null}

        {dataState.data && activeView === 'platinum' ? (
          <div className="prospecting-platinum prospecting-platinum-showcase">

            {platinumOffers.length && activeOffer ? (
              <div
                className={`prospecting-radial-shell${platinumDragging ? ' is-dragging' : ''}`}
                onPointerCancel={handlePlatinumPointerCancel}
                onPointerDown={handlePlatinumPointerDown}
                onPointerMove={handlePlatinumPointerMove}
                onPointerUp={handlePlatinumPointerUp}
                onWheel={handlePlatinumWheel}
              >

                <div
  className={`prospecting-radial-active-term${expandedPlanId === activeOffer.id ? ' is-expanded' : ''}${activeOffer.billingKey === 'annual' ? ' is-annual' : ''}`}
>
  <span
    className="prospecting-radial-active-term-text"
    key={activeOffer.id}
  >
    {BILLING_LABELS[activeOffer.billingKey]}
  </span>
</div>

                <div className="prospecting-radial-stage">

                  <div className="prospecting-radial-wheel">

                    {platinumOffers.map((offer, index) => {
                      const delta =
                        normalizePlatinumDelta(index)

                      const absDelta =
                        Math.abs(delta)

                      const isActive =
                        index === activeOfferIndex

                      const isExpanded =
                        isActive &&
                        expandedPlanId === offer.id

                      const angleStep =
                        360 / platinumOfferCount

                      const angle =
                        (
                          index -
                          platinumWheelStep
                        ) *
                        angleStep

                      const tilt =
                        Math.max(
                          -13,
                          Math.min(
                            13,
                            delta * 7,
                          ),
                        )

                      const scale =
                        isActive
                          ? 1
                          : absDelta === 1
                            ? 0.88
                            : absDelta === 2
                              ? 0.76
                              : 0.66

                      const billingClass =
                        offer.billingKey === 'sixMonths'
                          ? 'six-months'
                          : offer.billingKey

                      const tier =
                        offer.planIndex === 0
                          ? 'A'
                          : 'B'

                      const cardStyle: CSSProperties = {
                        transform:
                          `translate(-50%, -50%) rotate(${angle}deg) translateY(calc(-1 * var(--wf-platinum-radius))) rotate(${-angle + tilt}deg) scale(${scale})`,
                        zIndex:
                          isActive
                            ? 50
                            : Math.max(
                                1,
                                20 - absDelta,
                              ),
                      }

                      return (
                        <article
                          aria-expanded={isExpanded}
                          aria-pressed={isActive}
                          className={`prospecting-radial-card is-${billingClass} is-tier-${tier.toLowerCase()}${isActive ? ' is-active' : ''}${absDelta === 1 ? ' is-near' : ''}${absDelta >= 3 ? ' is-far' : ''}${isExpanded ? ' is-expanded' : ''}`}
                          key={offer.id}
                          onClick={() => {
                            if (
                              platinumSuppressClickRef.current
                            ) {
                              return
                            }

                            selectPlatinumOffer(index)
                          }}
                          onKeyDown={(event) => {
                            if (!isActive) return

                            if (
                              event.key === 'Enter' ||
                              event.key === ' '
                            ) {
                              event.preventDefault()
                              selectPlatinumOffer(index)
                            }
                          }}
                          role="button"
                          style={cardStyle}
                          tabIndex={isActive ? 0 : -1}
                        >

                          {offer.billingKey === 'annual' ? (
                            <div
                              aria-hidden="true"
                              className="prospecting-radial-wolf"
                            >
                              <WolfMark />
                            </div>
                          ) : null}

                          <div className="prospecting-radial-card-head">

                            <span className="prospecting-radial-tier">
                              <Diamond
                                aria-hidden="true"
                                size={17}
                              />
                              PLATINUM {tier}
                            </span>

                          </div>

                          {offer.billingKey === 'annual' ? (
                            <span className="prospecting-radial-best-value">
                              BEST VALUE
                            </span>
                          ) : null}

                          <h3>
                            {offer.plan.title}
                          </h3>

                          <p className="prospecting-radial-price">
                            {money(
                              offer.plan.prices[
                                offer.billingKey
                              ],
                            )}

                            <small>
                              {' '}/ {BILLING_LABELS[
                                offer.billingKey
                              ].toLowerCase()}
                            </small>
                          </p>

                          {isActive ? (
                            <span className="prospecting-radial-tap">
                              {isExpanded
                                ? 'Tap to close'
                                : 'Tap to view details'}
                            </span>
                          ) : null}

                          <div className="prospecting-radial-details">

                            {isActive ? (
                              <>
                                <ul>
                                  {offer.plan.features.map(
                                    (feature) => (
                                      <li key={feature}>
                                        <CheckCircle2
                                          aria-hidden="true"
                                          size={16}
                                        />
                                        {feature}
                                      </li>
                                    ),
                                  )}
                                </ul>

                                <button
                                  className="prospecting-radial-copy"
                                  onClick={(event) => {
                                    event.stopPropagation()

                                    void copyPlan(
                                      offer.plan.id,
                                      offer.billingKey,
                                    )
                                  }}
                                  onPointerDown={(event) => {
                                    event.stopPropagation()
                                  }}
                                  type="button"
                                >
                                  {copiedPlan === offer.plan.id ? (
                                    <>
                                      <Check size={17} />
                                      Copied
                                    </>
                                  ) : (
                                    <>
                                      <Copy size={17} />
                                      Copy Offer
                                    </>
                                  )}
                                </button>
                              </>
                            ) : null}

                          </div>

                        </article>
                      )
                    })}

                  </div>

                </div>

              </div>
            ) : (
              <div className="prospecting-raw">
                <pre>{dataState.data.platinumRaw}</pre>
              </div>
            )}

          </div>
        ) : null}
      </div>

      {selected && dataState.data ? (
        <DetailModal
          record={selected}
          memberships={dataState.data.memberships[selected.identityKey] ?? [selected.list]}
          inCrm={crmMembership.has(selected.identityKey)}
          onClose={() => setSelected(null)}
        />
      ) : null}
    </section>
  )
}



































